import { useCallback, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import sha256 from "crypto-js/sha256";
import { useSnackbar } from "notistack";
import _ from "lodash";
import axios from "axios";

import {
  getTXOneAccountUrl,
  getSSOClientId,
  getSSORedirectUri,
  generateUUID,
} from "utils";

const STATE_KEY = "mytxone:key";
const CODE_VERIFIER_KEY = "mytxone:cv";

export const useSSO = () => {
  const { search } = useLocation();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  // generates oauth login url to redirect to TXOne Account
  const getLoginUrl = useCallback(() => {
    const txoneAccountUrl = getTXOneAccountUrl();
    const resourceType = "code";
    const clientId = getSSOClientId();
    const redirectUri = getSSORedirectUri();
    const codeVerifier = generateUUID();
    const codeChallenge = btoa(sha256(codeVerifier)).replaceAll("=", "");
    const codeChallengeMethod = "S256";
    const approvalPrompt = "auto";
    const state = generateUUID();
    sessionStorage.setItem(STATE_KEY, state);
    sessionStorage.setItem(CODE_VERIFIER_KEY, codeVerifier);
    return `${txoneAccountUrl}/api/oauth2/authorize?resource_type=${resourceType}&client_id=${clientId}&redirect_uri=${redirectUri}&code_challenge=${codeChallenge}&code_challenge_method=${codeChallengeMethod}&state=${state}&approval_prompt=${approvalPrompt}`;
  }, []);

  // generates oauth register url to redirect to TXOne Account
  const getRegisterUrl = useCallback(() => {
    const txoneAccountUrl = getTXOneAccountUrl();
    const resourceType = "code";
    const clientId = getSSOClientId();
    const redirectUri = getSSORedirectUri();
    const codeVerifier = generateUUID();
    const codeChallenge = btoa(sha256(codeVerifier)).replaceAll("=", "");
    const codeChallengeMethod = "S256";
    const approvalPrompt = "auto";
    const state = generateUUID();
    sessionStorage.setItem(STATE_KEY, state);
    sessionStorage.setItem(CODE_VERIFIER_KEY, codeVerifier);
    return `${txoneAccountUrl}/signup?resource_type=${resourceType}&client_id=${clientId}&redirect_uri=${redirectUri}&code_challenge=${codeChallenge}&code_challenge_method=${codeChallengeMethod}&state=${state}&approval_prompt=${approvalPrompt}`;
  }, []);

  // runs oauth callback to get oauth tokens and gets mytxone session
  const login = useCallback(
    async (data) => {
      try {
        await axios.post("/api/v1/login", data);
        console.log("login success");
        sessionStorage.removeItem(STATE_KEY);
        sessionStorage.removeItem(CODE_VERIFIER_KEY);
        navigate("/overview");
      } catch (err) {
        console.err(err);
        enqueueSnackbar(_.get(err, "response.data.message", "Login failed"));
      }
    },
    [enqueueSnackbar, navigate]
  );

  // only runs once
  useEffect(() => {
    const searchParams = new URLSearchParams(search);
    const authCode = searchParams.get("auth_code");
    const state = searchParams.get("state");
    if (authCode && state && state === sessionStorage.getItem(STATE_KEY)) {
      login({
        code: authCode,
        redirectUri: getSSORedirectUri(),
        codeVerifier: sessionStorage.getItem(CODE_VERIFIER_KEY),
      });
    } else {
      sessionStorage.removeItem(CODE_VERIFIER_KEY);
      navigate("/");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, login]);

  return { getLoginUrl, getRegisterUrl };
};
